import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAuthContext } from "../../features/auth/AuthContext";
import { createAnalyticsHelpers } from "../utils/analytics";

export const useAnalytics = () => {
  const posthog = usePostHog();
  const { authState } = useAuthContext();
  const activityCounters = useRef<Record<string, number>>({});
  const sessionStartTime = useRef<number | null>(null);

  // Initialize analytics helpers
  const helpers = useMemo(() => {
    if (!posthog) return null;
    return createAnalyticsHelpers(posthog);
  }, [posthog]);

  // Track user activity for engagement scoring
  const trackActivity = useCallback((activityType: string, increment = 1) => {
    activityCounters.current[activityType] =
      (activityCounters.current[activityType] || 0) + increment;
  }, []);

  // Update user segments periodically
  const updateUserSegmentation = useCallback(() => {
    if (!helpers?.segmentation || !authState.userId) return;

    const userProperties = {
      user_id: authState.userId,
      login_time: authState.loginTime,
      last_activity: new Date().toISOString(),
      ...activityCounters.current,
    };

    helpers.segmentation.updateUserSegments(authState.userId, userProperties);
  }, [helpers, authState.userId, authState.loginTime]);

  // Calculate and track engagement score
  const updateEngagementScore = useCallback(() => {
    if (!helpers?.engagement || !authState.userId) return;

    const sessionDuration = sessionStartTime.current
      ? Date.now() - sessionStartTime.current
      : 0;

    const userActivity = {
      ...activityCounters.current,
      session_duration: sessionDuration,
      active_days: 1, // This would be calculated from historical data
    };

    const score = helpers.engagement.calculateEngagementScore(userActivity);
    helpers.engagement.trackEngagementScore(
      authState.userId,
      score,
      userActivity
    );
  }, [helpers, authState.userId]);

  // Track retention milestones
  const trackRetentionMilestone = useCallback(
    (milestone: string) => {
      if (!helpers?.retention || !authState.userId) return;

      const daysSinceLogin = authState.loginTime
        ? Math.floor((Date.now() - authState.loginTime) / (1000 * 60 * 60 * 24))
        : 0;

      helpers.retention.trackRetentionMilestone(authState.userId, milestone, {
        days_since_login: daysSinceLogin,
        total_sessions: activityCounters.current.total_sessions || 1,
        features_used: Object.keys(activityCounters.current).length,
      });
    },
    [helpers, authState.userId, authState.loginTime]
  );

  // Track user return
  const trackUserReturn = useCallback(
    (daysSinceLastVisit: number) => {
      if (!helpers?.retention || !authState.userId) return;

      helpers.retention.trackUserReturn(authState.userId, {
        days_since_last_visit: daysSinceLastVisit,
        session_count: activityCounters.current.total_sessions || 1,
        features_used_this_session: [],
      });
    },
    [helpers, authState.userId]
  );

  // Initialize session tracking
  useEffect(() => {
    if (authState.userId) {
      sessionStartTime.current = Date.now();
      trackActivity("total_sessions", 1);
    }
  }, [authState.userId, trackActivity]);

  // Update segmentation periodically
  useEffect(() => {
    if (!authState.userId) return;

    const interval = setInterval(updateUserSegmentation, 5 * 60 * 1000); // Every 5 minutes
    return () => clearInterval(interval);
  }, [authState.userId, updateUserSegmentation]);

  // Update engagement score before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      updateEngagementScore();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [updateEngagementScore]);

  // Track page views manually (since autocapture is disabled)
  const trackPageView = useCallback(
    (pageName: string, properties?: Record<string, unknown>) => {
      posthog?.capture("$pageview", {
        $current_url: window.location.href,
        page_name: pageName,
        ...properties,
      });
    },
    [posthog]
  );

  // Capture a custom event with optional properties
  const captureEvent = useCallback(
    (eventName: string, properties?: Record<string, unknown>) => {
      posthog?.capture(eventName, properties);
    },
    [posthog]
  );

  // Identify user in PostHog
  const identifyUser = useCallback(
    (userId: string, properties?: Record<string, unknown>) => {
      posthog?.identify(userId, properties);
    },
    [posthog]
  );

  // Reset PostHog identity
  const resetAnalytics = useCallback(() => {
    posthog?.reset();
  }, [posthog]);

  // Get current cookie consent
  const getConsent = useCallback(async () => {
    let stored = localStorage.getItem("cookie_consent");

    // Fallback to reading from HTTP cookie to support cross-subdomain logins
    if (!stored && typeof window !== "undefined") {
      if ("cookieStore" in window) {
        try {
          // biome-ignore lint/suspicious/noExplicitAny: Window.cookieStore is not in standard lib
          const cookie = await (window as any).cookieStore.get(
            "cookie_consent"
          );
          if (cookie) {
            stored = decodeURIComponent(cookie.value);
            // Sync it to localStorage for future use
            localStorage.setItem("cookie_consent", stored);
          }
        } catch (error) {
          console.error("Failed to read cookieStore", error);
        }
      } else if (typeof document !== "undefined") {
        const match = document.cookie.match(/(?:^|; )cookie_consent=([^;]*)/);
        if (match) {
          stored = decodeURIComponent(match[1]);
          // Sync it to localStorage for future use
          localStorage.setItem("cookie_consent", stored);
        }
      }
    }

    if (!stored) return null;
    try {
      return JSON.parse(stored) as { analytics: boolean; marketing: boolean };
    } catch {
      if (stored === "accepted") {
        return { analytics: true, marketing: false };
      }
      return null;
    }
  }, []);

  // Set cookie consent
  const setConsent = useCallback(
    async (consent: { analytics: boolean; marketing: boolean }) => {
      const value = JSON.stringify(consent);
      localStorage.setItem("cookie_consent", value);

      // Also set an HTTP cookie to persist across subdomains
      if (typeof window !== "undefined") {
        const expiry = new Date();
        expiry.setFullYear(expiry.getFullYear() + 1);

        if ("cookieStore" in window) {
          try {
            // biome-ignore lint/suspicious/noExplicitAny: Window.cookieStore is not in standard lib
            await (window as any).cookieStore.set({
              name: "cookie_consent",
              value: encodeURIComponent(value),
              expires: expiry.getTime(),
              path: "/",
              sameSite: "lax",
            });
          } catch (error) {
            console.error("Failed to write to cookieStore", error);
          }
        } else if (typeof document !== "undefined") {
          // biome-ignore lint/suspicious/noDocumentCookie: Fallback for older browsers
          document.cookie = `cookie_consent=${encodeURIComponent(
            value
          )}; expires=${expiry.toUTCString()}; path=/; SameSite=Lax`;
        }
      }

      if (consent.analytics) {
        posthog?.opt_in_capturing();
      } else {
        posthog?.opt_out_capturing();
      }
    },
    [posthog]
  );

  return {
    helpers,
    trackActivity,
    trackPageView,
    captureEvent,
    identifyUser,
    resetAnalytics,
    getConsent,
    setConsent,
    updateUserSegmentation,
    updateEngagementScore,
    trackRetentionMilestone,
    trackUserReturn,
    activityCounters: activityCounters.current,
  };
};

// Hook for specific feature tracking
export const useFeatureTracking = (featureName: string) => {
  const { trackActivity, helpers } = useAnalytics();
  const { authState } = useAuthContext();
  const featureSessionStart = useRef<number | null>(null);

  const startFeatureSession = useCallback(() => {
    featureSessionStart.current = Date.now();
    trackActivity(`${featureName}_sessions`, 1);
  }, [featureName, trackActivity]);

  const endFeatureSession = useCallback(() => {
    if (featureSessionStart.current) {
      const duration = Date.now() - featureSessionStart.current;
      trackActivity(`${featureName}_duration`, duration);
      featureSessionStart.current = null;
    }
  }, [featureName, trackActivity]);

  const trackFeatureAction = useCallback(
    (action: string, properties?: Record<string, unknown>) => {
      trackActivity(`${featureName}_${action}`, 1);

      if (helpers?.segmentation && authState.userId) {
        helpers.segmentation.trackSegmentEvent(`${featureName}_${action}`, {
          user_id: authState.userId,
          feature: featureName,
          action,
          timestamp: new Date().toISOString(),
          ...properties,
        });
      }
    },
    [featureName, trackActivity, helpers, authState.userId]
  );

  return {
    startFeatureSession,
    endFeatureSession,
    trackFeatureAction,
  };
};
